/**
 * Private-memory `oidc-spa` adapter for the #3882 SSO client (T022).
 *
 * Per `contracts/oidc-provider.md` ("Normal Dashboard Configuration") and research.md §4/§7/§9:
 * this module configures the current approved `oidc-spa` release for NORMAL discovery from the
 * SSO authority/auth URL (T012's `getOidcConfig().authority`, passed as `issuerUri`) plus the
 * public client ID — with NO explicit authorize/token/userinfo/issuer/JWKS/end-session endpoint
 * overrides (`ParamsOfCreateOidc.__metadata` is deliberately never used here). `oidc-spa`'s own
 * flow already sends state + PKCE `S256` and never sends `nonce` unless a caller explicitly
 * configures one (see the compiled `oidc-client-ts` vendor bundle: `nonce` is only appended to
 * the authorization URL "if (nonce)" — this adapter never passes one), which matches the
 * delivered #3882 profile's rejection of `nonce`.
 *
 * Scope (T022 only): configure the client and relay whatever tokens `oidc-spa` reports into
 * `auth-boundary.ts`'s `setSsoCredentials()` facade (T017) — never into Redux, never into
 * `localStorage`. The provider's refresh token never reaches `oidc-spa` at all: before the client
 * is created, `token-validation.ts`'s `installRefreshTokenStripper` removes `refresh_token` from
 * token-endpoint responses, so `oidc-client-ts` has none to store or renew with and renewal can only
 * use the silent `prompt=none` attempt (T063). `SsoCredentials` (T017) has no refresh-token field
 * either, and `publishTokens` below never reads `refreshToken`/`hasRefreshToken`. Renewal-episode orchestration (single
 * shared `prompt=none` attempt) is T027's job — this module only exposes the underlying
 * `renewTokens` primitive oidc-spa already provides (itself internally mutexed to one in-flight
 * call; see createOidc.js's `renewTokens_mutexed`), correctly configured with no extra overrides.
 * Client-scoped `end_session` sequencing (Dashboard-local cleanup first) is T017/a later logout
 * task's job — this module only exposes the `logout` primitive oidc-spa derives from the
 * discovered `end_session_endpoint`, which oidc-client-ts's `signoutRedirect` already scopes to
 * this client's registered `client_id`/post-logout redirect URI.
 */

import { createOidc, type Oidc, type ParamsOfCreateOidc } from 'oidc-spa/core';

import { OIDC_BOOT_TIMEOUT_MS, withTimeout } from './bounded';
import { getAppRootUrl, validateOidcConfig, type OidcConfig } from './oidc-config';
import { RumEvent } from '../../shared/analytics/rum';
import {
  createIdTokenSchema,
  fetchDiscoveredProviderMetadata,
  installRefreshTokenStripper,
  isIdTokenFresh,
  IdTokenValidationError,
  type DiscoveredProviderMetadata,
} from './token-validation';

/**
 * The narrow, non-generic shape this adapter actually needs from `createOidc` — `oidc-spa`'s own
 * type is generic over an optional `decodedIdTokenSchema`-derived `DecodedIdToken`/`AutoLogin`,
 * which this adapter does not use (it only reads the OIDC-core-spec claims), so a fixed
 * `Oidc.Tokens.DecodedIdToken_OidcCoreSpec` here keeps `createOidcImpl` simple to inject in tests.
 */
export type CreateOidcFn = (params: ParamsOfCreateOidc) => Promise<Oidc>;

/** The subset of `AuthBoundary` this adapter needs — avoids a hard import cycle risk and keeps this module trivially mockable. */
export interface OidcClientAuthBoundary {
  setSsoCredentials(credentials: {
    accessToken: string;
    idToken?: string | null;
    expiresAt?: number | null;
  }): void;
}

export interface OidcClientDependencies {
  /**
   * Defaults to the validated build configuration (T012's `validateOidcConfig()`). An invalid or
   * missing configuration is reported through {@link reportInvalidConfig}, then rejects.
   */
  readonly config?: OidcConfig;
  /** The app root to validate the redirect URIs against; defaults to the address this page runs at. */
  readonly appRootUrl?: string;
  /** Defaults to {@link recordInvalidOidcConfig}, once per page load (insightt-tickets#4256). */
  readonly reportInvalidConfig?: (report: InvalidOidcConfigReport) => void;
  /** Publishes tokens into the private in-memory envelope. Never Redux, never `localStorage`. */
  readonly authBoundary: OidcClientAuthBoundary;
  /**
   * Defaults to the real `oidc-spa` `createOidc`. Overridable so callers (and this task's own
   * contract test) can inject a fake without performing a real network discovery round-trip for
   * the token-handling assertions; the discovery-shape assertions instead exercise this exact
   * default against a fetch-mocked discovery/JWKS document (see
   * `__tests__/oidc-client-contract.test.ts`).
   */
  readonly createOidcImpl?: CreateOidcFn;
  /** Defaults to {@link fetchDiscoveredProviderMetadata}; supplies the issuer the ID token must match. */
  readonly fetchProviderMetadata?: (authority: string) => Promise<DiscoveredProviderMetadata>;
  /** Defaults to {@link installRefreshTokenStripper}; keeps refresh tokens out of `oidc-client-ts`. */
  readonly installTokenResponseFilter?: (tokenEndpoint: string) => void;
}

/**
 * The narrow surface later tasks (T024 callback consumption, T025 boundary bootstrap, T027
 * renewal, and a later logout task) are expected to consume. Deliberately not the full `oidc-spa`
 * `Oidc` union — callers should not need to branch on `isUserLoggedIn` themselves for anything
 * beyond what is exposed here.
 */
export interface SsoOidcClient {
  readonly issuerUri: string;
  readonly clientId: string;
  readonly isUserLoggedIn: boolean;
  /** Starts an explicit Authorization Code + PKCE handoff, including for a restored session. */
  readonly login: Oidc.NotLoggedIn['login'];
  /**
   * The single oidc-spa-provided renewal primitive (already internally single-flight). Present
   * only when logged in.
   *
   * **Timing guarantee, verified against the compiled `node_modules/oidc-spa/core/createOidc.js`
   * (v10.2.11):** the returned promise resolves strictly AFTER this client's
   * `subscribeToTokensChange` callback (`createSsoOidcClient()`'s `publishTokens` below) has
   * already fired with the renewed tokens. `renewTokens_nonMutexed` (the function
   * `renewTokens_mutexed`/the exported `renewTokens` closure ultimately awaits) synchronously calls
   * `Array.from(onTokenChanges).forEach(onTokenChange => onTokenChange(currentTokens))` as its
   * last statement before returning — there is no `await` after that line, so the async function's
   * own promise cannot settle until that synchronous dispatch has already run. This means T027's
   * `renewSsoSession` (see `auth-boundary.ts`) can safely do `await client.renewTokens()` then read
   * the auth boundary's OWN already-updated envelope back out, instead of trying to thread
   * `renewTokens()`'s resolved value (which carries no return value at all — it resolves
   * `void`/`undefined`) through as `SsoCredentials`.
   *
   * **Failure-path caveat (also verified against the same file):** `renewTokens()` never actually
   * rejects. Every failure outcome inside `renewTokens_nonMutexed` (`loginSilent`'s `"timeout"`,
   * `"other error"`, or `"got error auth response using refresh token"` outcomes, and the
   * no-refresh-token-and-no-iframe case) routes into either `window.location.reload()` or
   * `fallbackToFullPageReload()` (a real top-level redirect to the OIDC authority via
   * `loginOrGoToAuthServer`), then hangs forever (`await new Promise(() => {})`) — the promise
   * simply never settles on failure; the page navigates away instead. This is pre-existing,
   * always-on behavior of `oidc-spa` itself (see `scheduleTokenRefreshToKeepSessionAlive` inside
   * `createOidc.js`, which already calls this exact `renewTokens()` on its own background timer
   * regardless of whether anything in this app ever calls it), not something introduced by wiring
   * T027's `renewSsoSession` to it. Practical effect: a genuine silent-renewal failure is observed as
   * the browser handing off to the SSO sign-in with a full-page redirect, which is accepted behavior
   * (spec.md 2026-09-30 clarification, FR-014). `ensureFreshSession()` bounds the attempt with
   * `SILENT_RENEWAL_TIMEOUT_MS`, so a hang settles as `interaction_required` instead of waiting.
   */
  readonly renewTokens: Oidc.LoggedIn['renewTokens'] | undefined;
  /** Discovered, client-scoped `end_session` primitive. Present only when logged in. */
  readonly logout: Oidc.LoggedIn['logout'] | undefined;
}

/**
 * Builds the override-free `createOidc` params from T012's public config (research.md §4), plus
 * the Dashboard's ID token checks (T062) when the discovered issuer is known.
 */
export function buildCreateOidcParams(
  config: OidcConfig,
  metadata?: DiscoveredProviderMetadata,
): ParamsOfCreateOidc {
  return {
    issuerUri: config.authority,
    clientId: config.clientId,
    ...(metadata
      ? { decodedIdTokenSchema: createIdTokenSchema({ issuer: metadata.issuer, clientId: config.clientId }) }
      : {}),
  };
}

/**
 * Publishes freshly obtained oidc-spa tokens into the auth boundary, discarding anything the
 * provider response carried beyond access/ID token and expiry (data-model.md §6, research.md §7):
 * `tokens.refreshToken`/`tokens.hasRefreshToken` are never read here, so a returned refresh token
 * never leaves this function's local scope, is never persisted, and never reaches another module.
 */
function publishTokens(authBoundary: OidcClientAuthBoundary, tokens: Oidc.Tokens): void {
  authBoundary.setSsoCredentials({
    accessToken: tokens.accessToken,
    idToken: tokens.idToken ?? null,
    expiresAt: tokens.accessTokenExpirationTime ?? null,
  });
}

// A boot hint only: tokens and the Backend session still establish authentication. oidc-spa
// keeps no auth-state record when iframe restoration is available (#4207). Share the boolean
// across windows so closing the original window does not lose session restoration (#4272).
const SSO_BOOT_HINT_PREFIX = 'insightt:sso-boot:';

function setSsoBootHint(clientId: string, active: boolean): void {
  const key = `${SSO_BOOT_HINT_PREFIX}${clientId}`;
  for (const storageName of ['localStorage', 'sessionStorage'] as const) {
    try {
      const storage = window[storageName];
      if (active) storage.setItem(key, 'true');
      else storage.removeItem(key);
    } catch {
      // Either store can be unavailable independently; keep the accessible one working.
    }
  }
}

/**
 * Why this build's SSO configuration was rejected (insightt-tickets#4256). Every value is public build
 * configuration already shipped in the bundle (the address the page runs at, and validation messages
 * that name configured URLs and field names); it never carries tokens or user data.
 */
export interface InvalidOidcConfigReport {
  readonly appRootUrl: string;
  readonly errorCount: number;
  readonly errors: readonly string[];
}

let invalidOidcConfigRecorded = false;

/**
 * Records a rejected SSO configuration in monitoring, once per page load: a failed client is retried
 * (boot, then the gateway's handoff, then "Try again"), and each retry fails for the same reason.
 * Loaded on demand so the monitoring module is only imported when there is something to report.
 */
export function recordInvalidOidcConfig(report: InvalidOidcConfigReport): void {
  if (invalidOidcConfigRecorded) return;
  invalidOidcConfigRecorded = true;
  void import('../../shared/monitoring').then(({ getAwsRum }) =>
    getAwsRum().recordEvent(RumEvent.SSO_CONFIG_INVALID, { ...report }),
  );
}

/** Test-only reset for {@link recordInvalidOidcConfig}'s once-per-page-load guard. */
export function resetInvalidOidcConfigReportForTesting(): void {
  invalidOidcConfigRecorded = false;
}

/**
 * The validated build configuration. A rejected one (for example a PR preview whose address is not in
 * the redirect URI list) is reported with the reason, then throws as `getOidcConfig()` does, so the
 * user still sees the existing "unavailable" recovery screen.
 */
function resolveOidcConfig(dependencies: OidcClientDependencies): OidcConfig {
  const appRootUrl = dependencies.appRootUrl ?? getAppRootUrl();
  const result = validateOidcConfig({ appRootUrl });
  if (result.valid) return result.config;
  (dependencies.reportInvalidConfig ?? recordInvalidOidcConfig)({
    appRootUrl,
    errorCount: result.errors.length,
    errors: result.errors,
  });
  throw new Error(`Invalid OIDC configuration: ${result.errors.join('; ')}`);
}

/**
 * Creates the private-memory `oidc-spa` adapter (T022). Publishes the current tokens (when a
 * session is already active on load) and subscribes to future token changes so every renewal
 * result oidc-spa reports is relayed the same way — without adding any renewal-retry or
 * auto-redirect behavior of its own (that stays out of scope; see module doc above).
 */
export async function createSsoOidcClient(dependencies: OidcClientDependencies): Promise<SsoOidcClient> {
  const config = dependencies.config ?? resolveOidcConfig(dependencies);
  const createOidcImpl: CreateOidcFn = dependencies.createOidcImpl ?? createOidc;
  const { authBoundary } = dependencies;

  const metadata = await (dependencies.fetchProviderMetadata ?? fetchDiscoveredProviderMetadata)(config.authority);
  // Installed before `createOidc()` so the boot-time code exchange is already filtered (T063).
  (dependencies.installTokenResponseFilter ?? installRefreshTokenStripper)(metadata.tokenEndpoint);

  const oidc = await withTimeout(
    createOidcImpl(buildCreateOidcParams(config, metadata)),
    OIDC_BOOT_TIMEOUT_MS,
    'SSO client initialization',
  );

  if (!oidc.isUserLoggedIn) {
    setSsoBootHint(config.clientId, false);
    // `oidc-spa` never rejects on an initialization failure (e.g. the IdP refusing to restore a
    // stored session): it resolves "not logged in" with `initializationError` set and swaps `login`
    // for a function that `alert()`s and then never settles (`createOidc.ts`'s `not_loggedIn_case`).
    // Rejecting here routes both callers to their existing failure paths — `runSsoBoot`'s
    // `boot_failed` and `AuthenticationSsoHandoff`'s recovery screen — instead of a permanent hang.
    if (oidc.initializationError) {
      throw oidc.initializationError;
    }

    return {
      issuerUri: oidc.issuerUri,
      clientId: oidc.clientId,
      isUserLoggedIn: false,
      login: oidc.login,
      renewTokens: undefined,
      logout: undefined,
    };
  }

  const tokens = await oidc.getTokens();
  // Tokens just returned from the provider must not already be expired (T062). A session restored
  // from storage legitimately carries an older ID token, so only a fresh callback is checked here.
  if (oidc.backFromAuthServer !== undefined && !isIdTokenFresh(tokens.decodedIdToken_original)) {
    throw new IdTokenValidationError('token has expired');
  }
  publishTokens(authBoundary, tokens);
  setSsoBootHint(config.clientId, true);

  // Renewed tokens are always freshly issued; an expired set is never published (T062).
  oidc.subscribeToTokensChange((nextTokens) => {
    if (isIdTokenFresh(nextTokens.decodedIdToken_original)) {
      publishTokens(authBoundary, nextTokens);
    }
  });

  return {
    issuerUri: oidc.issuerUri,
    clientId: oidc.clientId,
    isUserLoggedIn: true,
    // A session restored during a gateway submission must still honor that explicit sign-in.
    login: (params) => oidc.goToAuthServer(params ?? {}),
    renewTokens: oidc.renewTokens,
    logout: oidc.logout,
  };
}

const OIDC_SPA_AUTH_STATE_PREFIX = 'oidc-spa:auth-state:';
const OIDC_SPA_SESSION_TOKENS_PREFIX = 'oidc-spa:lazy-session-storage:';
const EXPLICITLY_LOGGED_OUT_STATE = JSON.stringify({
  __brand: 'PersistedAuthState-v1',
  stateDescription: 'explicitly logged out',
});

/**
 * Makes `oidc-spa` treat this browser as explicitly signed out, part of cleanup's local clearing
 * (FR-021, FR-022; T065, T087). Without it, a logout whose provider `end_session` could not be
 * confirmed leaves the provider session alive, and the next page load silently restores it. With it,
 * `createOidc()` skips session restoration and the next sign-in asks for credentials.
 *
 * Writes `oidc-spa`'s own persisted-state record (`core/persistedAuthState.js`, keyed by
 * `core/configId.js`'s `issuerUri:clientId`) and removes any tokens it kept in `sessionStorage`.
 * Re-check this format on every `oidc-spa` upgrade.
 */
export function markSsoSessionEndedLocally(config: Pick<OidcConfig, 'authority' | 'clientId'>): void {
  setSsoBootHint(config.clientId, false);
  // Only this client's records: the provider contract keeps other clients' sessions active. The
  // issuer part is matched loosely because `oidc-spa`'s issuer URI can differ from the configured
  // authority (for example by a trailing slash).
  const authStateSuffix = `:${config.clientId}`;
  try {
    const local = window.localStorage;
    const keys = new Set(
      Object.keys(local).filter(
        (key) => key.startsWith(OIDC_SPA_AUTH_STATE_PREFIX) && key.endsWith(authStateSuffix),
      ),
    );
    keys.add(`${OIDC_SPA_AUTH_STATE_PREFIX}${config.authority}${authStateSuffix}`);
    keys.forEach((key) => {
      try {
        local.setItem(key, EXPLICITLY_LOGGED_OUT_STATE);
      } catch {
        // A full store can still allow removal. Do not leave a stale "logged in" record behind.
        try {
          local.removeItem(key);
        } catch {
          // Blocked storage must not prevent cleanup of other records or cached tokens.
        }
      }
    });
  } catch {
    // Access to localStorage can be blocked independently of sessionStorage.
  }

  const sessionTokensMarker = `${authStateSuffix}:`;
  try {
    const session = window.sessionStorage;
    Object.keys(session)
      .filter((key) => key.startsWith(OIDC_SPA_SESSION_TOKENS_PREFIX) && key.includes(sessionTokensMarker))
      .forEach((key) => {
        try {
          session.removeItem(key);
        } catch {
          // Continue clearing any other accessible token records.
        }
      });
  } catch {
    // Provider storage is best-effort; the boundary must still clear its in-memory credentials.
  }
}

/**
 * True when this browser has completed SSO or holds `oidc-spa`'s "logged in" record, so boot
 * restores through the provider whatever the rollout control says (#4193 FR-008). The shared
 * hint contains no credentials and never authorizes a request. The tab hint supports existing
 * sessions and browsers where localStorage is unavailable. The library records match the same
 * `oidc-spa:auth-state:<issuer>:<clientId>` records {@link markSsoSessionEndedLocally} writes (any
 * issuer spelling); storage or parse errors read `false`. Re-check this format on every `oidc-spa`
 * upgrade.
 */
export function hasSsoSessionRecord(clientId: string): boolean {
  try {
    if (window.sessionStorage.getItem(`${SSO_BOOT_HINT_PREFIX}${clientId}`) === 'true') return true;
  } catch {
    // sessionStorage can be blocked independently of the shared hint and library records.
  }
  try {
    const local = window.localStorage;
    if (local.getItem(`${SSO_BOOT_HINT_PREFIX}${clientId}`) === 'true') return true;
    return Object.keys(local).some((key) => {
      if (!key.startsWith(OIDC_SPA_AUTH_STATE_PREFIX) || !key.endsWith(`:${clientId}`)) return false;
      const record: unknown = JSON.parse(local.getItem(key) ?? 'null');
      return (record as { stateDescription?: unknown } | null)?.stateDescription === 'logged in';
    });
  } catch {
    return false;
  }
}

/** {@link hasSsoSessionRecord} for this build's configured client; an invalid configuration reads `false`. */
export function hasConfiguredSsoSessionRecord(): boolean {
  const { config } = validateOidcConfig();
  return config ? hasSsoSessionRecord(config.clientId) : false;
}

/**
 * Boot-time shared singleton wrapper around {@link createSsoOidcClient}.
 *
 * Post-T024-defect-fix redesign (see `main.tsx`'s and `AuthenticationRouter.tsx`'s doc comments):
 * `oidc-spa` always redirects a real login back to this app's root, never to a dedicated callback
 * route (`node_modules/oidc-spa/README.md` line 67; `createOidc.js`'s hardcoded
 * `homeUrlAndRedirectUri`), so `createSsoOidcClient()` must be invoked unconditionally at app
 * boot — the only place a real callback response is ever actually observed — rather than lazily
 * inside a route/gateway-decision branch. `AuthenticationRouter.tsx`'s `AuthenticationSsoHandoff`
 * (the gateway's `SSO`-decision branch) also needs a client to call `login()` on. Both call sites
 * share this one module-level promise instead of each constructing a competing client:
 * `createOidc()` itself already memoizes per `configId` for the page's lifetime (see
 * `createOidc.js`'s `prOidcByConfigId` map), so a second `createSsoOidcClient()` call would not be
 * unsafe, but it would redundantly re-run `getTokens()`/`subscribeToTokensChange()` and re-publish
 * credentials a second time.
 */
let sharedSsoOidcClientPromise: Promise<SsoOidcClient> | null = null;

/** Returns the shared boot-time client, creating it on first call and reusing it thereafter. */
export function getSharedSsoOidcClient(dependencies: OidcClientDependencies): Promise<SsoOidcClient> {
  if (!sharedSsoOidcClientPromise) {
    // A failed attempt is not cached, so the gateway's handoff can retry after a provider outage
    // at boot (T106).
    sharedSsoOidcClientPromise = createSsoOidcClient(dependencies).catch((error: unknown) => {
      sharedSsoOidcClientPromise = null;
      throw error;
    });
  }
  return sharedSsoOidcClientPromise;
}

/** Test-only escape hatch, mirrors `auth-boundary.ts`'s `resetAppAuthBoundaryForTesting`. */
export function resetSharedSsoOidcClientForTesting(): void {
  sharedSsoOidcClientPromise = null;
}
